import { useRouteRef } from '@backstage/core-plugin-api';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import type { HeaderBreadcrumb, HeaderMetadataItem } from '@backstage/ui';
import { PluginPage } from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { createTechdocsRootRef } from '../routes';
import { DOCS_SURFACE_OWNER_ENTITY_REF } from '../types';
import { useDocsView } from '../hooks/useDocsView';
import { DocsBackLink } from './DocsBackLink';
import { DocsLayout } from './DocsLayout';
import { DocsHomeView } from './DocsHomeView';
import { DocsCategoryView } from './DocsCategoryView';
import { QueryClientProvider } from './QueryClientProvider';
import styles from './DocsIndexPage.module.css';

// An empty string stops PluginPage from showing the description of the
// Backstage TechDocs plugin, and keeps the header height equal to a doc page.
const NO_DESCRIPTION = '';

// The owner of the Backstage TechDocs plugin has no Slack channel. The owner row also
// keeps the header height equal to a doc page.
const OWNER_ENTITY_REFS = [DOCS_SURFACE_OWNER_ENTITY_REF];
const OWNER_METADATA: HeaderMetadataItem[] = [
  {
    label: 'Owner',
    value: (
      <EntityRefLinks entityRefs={OWNER_ENTITY_REFS} defaultKind="group" />
    ),
  },
];

/** The docs home and category pages share `/docs`, with `filters[view]` selecting a category. */
export const DocsIndexPage = () => (
  <QueryClientProvider>
    <DocsIndexPageContent />
  </QueryClientProvider>
);

function DocsIndexPageContent() {
  const docType = useDocsView();
  const getTechdocsRootPath = useRouteRef(createTechdocsRootRef);
  const breadcrumbs: HeaderBreadcrumb[] | undefined = docType
    ? [{ label: 'Documentation', href: getTechdocsRootPath() }]
    : undefined;

  return (
    // Full width, not the default 1200px column: the docs sidebar sits on the
    // left of every docs page, and the reader page also needs room for the
    // fixed table of contents of TechDocs on the right. The index page uses
    // the same width, so that the sidebar does not move when a doc opens.
    <PluginPage
      width="full"
      title={docType ? docType.title : 'Docs home'}
      description={NO_DESCRIPTION}
      ownerEntityRefs={OWNER_ENTITY_REFS}
      metadata={OWNER_METADATA}
      breadcrumbs={breadcrumbs}
    >
      <DocsLayout>
        <div className={styles.content}>
          {breadcrumbs && <DocsBackLink to={breadcrumbs[0]} />}
          {/* The key gives each category a new entity list, so a list never
              shows the docs of the category before it. */}
          {docType ? (
            <DocsCategoryView key={docType.type} docType={docType} />
          ) : (
            <DocsHomeView />
          )}
        </div>
      </DocsLayout>
    </PluginPage>
  );
}
